import type { CityConfig } from "@/pages/city-template/types";

export const greshamConfig: CityConfig = {
  city: "Gresham",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "gresham",
  route: "/cities/gresham",
  title: "Emergency Passport Gresham, OR | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Gresham, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for east Portland metro and Multnomah County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Gresham, Oregon",
  heroSubtitle: "Passport expired before your trip to Canada? Flying out of PDX in days? Fast Passport Center helps Gresham travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Gresham%20Oregon%20suburban%20landscape%20with%20Mount%20Hood%20visible%20in%20background%2C%20residential%20neighborhoods%20with%20Pacific%20Northwest%20greenery%2C%20Sandy%20River%20valley%20in%20foreground%2C%20soft%20afternoon%20light%20with%20dramatic%20clouds%2C%20photorealistic%20landscape%20photography%20showing%20east%20Portland%20metro%20area%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=gresham-hero-01&orientation=landscape",
  heroImageAlt: "Gresham Oregon suburban landscape with Mount Hood and Sandy River valley",
  geoPosition: "45.4983;-122.4313",
  geoPlacename: "Gresham, Oregon",
  lastModified: "2026-05-29",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "300 5th Ave, Suite 600, Seattle, WA 98104",
    distance: "About 175 miles north — 3 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Gresham", zips: "97030, 97080" },
    { name: "Troutdale", zips: "97060" },
    { name: "Fairview", zips: "97024" },
    { name: "Wood Village", zips: "97060" },
    { name: "Boring", zips: "97009" },
    { name: "Sandy", zips: "97055" },
    { name: "Happy Valley", zips: "97086" },
    { name: "Oregon City", zips: "97045" },
    { name: "Milwaukie", zips: "97222, 97267" },
    { name: "Portland", zips: "97201–97299" },
    { name: "Damascus", zips: "97089" },
    { name: "Estacada", zips: "97023" },
    { name: "Clackamas", zips: "97015" },
    { name: "Gladstone", zips: "97027" },
    { name: "West Linn", zips: "97068" },
    { name: "Lake Oswego", zips: "97034–97035" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in Gresham in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Gresham", desc: "FedEx and UPS locations throughout Gresham and Troutdale offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — especially important for last-minute passport needs." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Carlos M.",
      location: "Gresham, OR",
      rating: 5,
      text: "Family trip to Vancouver BC in 10 days and my passport expired. Fast Passport Center renewed it in 6 days. I shipped from the FedEx on Burnside in Gresham. Couldn't have been easier.",
      service: "Passport Renewal",
    },
    {
      name: "Amanda T.",
      location: "Troutdale, OR",
      rating: 5,
      text: "Cruise to Alaska in two weeks and my daughter's passport was missing. Fast Passport Center walked us through the child passport replacement process. We had it in 8 days. Troutdale mom approved.",
      service: "Child Passport",
    },
    {
      name: "Robert J.",
      location: "Sandy, OR",
      rating: 5,
      text: "Business trip to Calgary announced with a week's notice. Fast Passport Center handled the emergency processing and I had my passport in 4 days. The daily updates were reassuring.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "gresham-main",
      name: "Gresham Main Post Office",
      type: "USPS Passport Acceptance",
      address: "224 W Powell Blvd",
      city: "Gresham",
      state: "OR",
      zip: "97030",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm, Sat 9am–12pm",
      distance: "Downtown Gresham",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Gresham. Appointment required — book online in advance. Photo services may be available.",
      icon: "ri-mail-line",
    },
    {
      id: "gresham-east",
      name: "Gresham East Station",
      type: "USPS Passport Acceptance",
      address: "19231 E Burnside St",
      city: "Gresham",
      state: "OR",
      zip: "97030",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "East Gresham — near Burnside",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "East Gresham passport acceptance option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "troutdale-post",
      name: "Troutdale Post Office",
      type: "USPS Passport Acceptance",
      address: "667 NW Frontage Rd",
      city: "Troutdale",
      state: "OR",
      zip: "97060",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Troutdale — about 5 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Troutdale / Columbia River Gorge option for west Gresham area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "sandy-post",
      name: "Sandy Post Office",
      type: "USPS Passport Acceptance",
      address: "16630 Champion Way",
      city: "Sandy",
      state: "OR",
      zip: "97055",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Sandy — about 15 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Sandy / Mount Hood area option for east Gresham residents.",
      icon: "ri-mail-line",
    },
    {
      id: "oregon-city-post",
      name: "Oregon City Post Office",
      type: "USPS Passport Acceptance",
      address: "515 High St",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Oregon City — about 12 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Clackamas County option for south Gresham and Happy Valley residents.",
      icon: "ri-mail-line",
    },
    {
      id: "clackamas-county",
      name: "Clackamas County Clerk",
      type: "County Passport Acceptance",
      address: "1710 Red Soils Ct, Suite 100",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "503-655-8551",
      hours: "Mon–Fri 8am–5pm (by appointment)",
      distance: "Oregon City — Government Center",
      website: "https://www.clackamas.us/recording",
      websiteLabel: "Clackamas County Clerk",
      notes: "Clackamas County clerk passport acceptance. Appointment required — may offer faster scheduling than USPS. Call to confirm passport services.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Gresham before international travel?",
      a: "Yes. Through Fast Passport Center, Gresham travelers can get an expedited passport in as little as 3–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today — ideal for east Portland metro and Multnomah County travel plans.",
    },
    {
      q: "How fast can I renew my passport in Gresham?",
      a: "Fast passport renewal in Gresham is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires before my trip to Canada?",
      a: "If your passport expires before a trip to Canada, don't wait. Canada requires your passport to be valid for the duration of your stay. Fast Passport Center can expedite your renewal so you don't miss your flight or cruise. The sooner you start, the more options you have.",
    },
    {
      q: "Can I get a same-day passport appointment near Gresham?",
      a: "Same-day passport appointments at the regional passport agency in Seattle are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near Gresham, Fast Passport Center is the faster and more predictable option — no Seattle drive required.",
    },
    {
      q: "Can I expedite a child passport in Gresham?",
      a: "Yes. Child passport expediting in Gresham is possible through Fast Passport Center, but child passports (for minors 15 and under) do require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen before an upcoming trip, act immediately. You will need to complete Form DS-64 to report it lost or stolen and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps.",
    },
    {
      q: "Does Gresham have a passport agency?",
      a: "Gresham, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 175 miles north) and the San Francisco Passport Agency in California (about 640 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster and more convenient solution for most Gresham residents.",
    },
    {
      q: "How long does a standard passport take in Gresham?",
      a: "If you apply through a standard post office acceptance facility in Gresham, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner than that, you need expedited processing. Fast Passport Center offers Gresham travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I get a passport renewal near me in Gresham without visiting an office?",
      a: "Yes. Most Gresham-area adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in Gresham or Troutdale.",
    },
    {
      q: "What airports do Gresham travelers typically fly from?",
      a: "Gresham travelers typically use Portland International Airport (PDX) — about 18 miles west — for all domestic and international departures. PDX offers direct flights to Canada, Europe, and Asia. If your passport isn't in hand, don't book international travel. Fast Passport Center can help you meet your deadline.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Gresham", address: "2755 NE Hogan Dr, Gresham, OR 97030", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Gresham", address: "1005 NW Civic Dr, Gresham, OR 97030", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Gresham", address: "2005 NW Burnside Rd, Gresham, OR 97030", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM" },
    { name: "The UPS Store — Gresham", address: "2455 NW Division St, Gresham, OR 97030", hours: "Mon–Fri 8 AM – 7 PM" },
    { name: "Walmart — Gresham", address: "39000 Proctor Blvd, Sandy, OR 97055", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Troutdale", address: "1001 NW Frontage Rd, Troutdale, OR 97060", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Troutdale", address: "2563 SW Cherry Park Rd, Troutdale, OR 97060", hours: "Daily 7 AM – 10 PM" },
    { name: "Walgreens — Oregon City", address: "1900 Beavercreek Rd, Oregon City, OR 97045", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Oregon City", address: "2000 Beavercreek Rd, Oregon City, OR 97045", hours: "Daily 7 AM – 10 PM" },
    { name: "Walmart — Oregon City", address: "16301 SE 82nd Dr, Clackamas, OR 97015", hours: "Daily 7 AM – 11 PM" },
  ],
  dmvLocations: [
    {
      name: "Gresham DMV Office",
      address: "1005 NW Frontage Rd, Troutdale, OR 97060",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Multnomah County DMV office near Gresham. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Oregon City DMV Office",
      address: "1651 Beavercreek Rd, Oregon City, OR 97045",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Clackamas County option for south Gresham and Oregon City area residents.",
      featured: false,
    },
    {
      name: "Portland DMV Office — McLoughlin",
      address: "9301 SE McLoughlin Blvd, Portland, OR 97222",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Portland option for west Gresham metro residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — about 18 miles west",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX offers direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. The busiest travel seasons are May–August and December–January.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 175 miles north",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some Gresham travelers use SEA for additional flight options. Passport required for all international departures.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
    {
      code: "TTD",
      name: "Portland-Troutdale Airport",
      distance: "General aviation — about 5 miles west",
      address: "2023 E 223rd Ave, Gresham, OR 97030",
      note: "Portland-Troutdale Airport is general aviation only — no commercial flights. International travelers use PDX.",
      flightUrl: "https://www.portofportland.com/ttd",
      flightLabel: "TTD Airport Info",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from PDX in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Gresham and the east Portland metro.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Gresham and east Portland metro travelers renew fast — perfect for family vacations and business trips.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Gresham", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Gresham travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Gresham families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Gresham residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines. Same-day passport help for Gresham travelers.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with the same expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Gresham, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Gresham residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Gresham, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Gresham travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Gresham families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Gresham, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Gresham travelers and families often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip, especially for summer and holiday travel from PDX." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before your documents are submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission, critical for last-minute passport renewal in Gresham." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Gresham runs 6–13 weeks. If your trip to Canada, Europe, or Mexico is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly — especially for international flights out of PDX. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Gresham travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Gresham", description: "Drop off at any FedEx or UPS in the Gresham or Troutdale area — most locations are open evenings. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3 hours each way)", fpc: "Ship documents overnight from Gresham" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/cities/portland" },
    { name: "Fast passport renewal in Beaverton", route: "/cities/beaverton" },
    { name: "Emergency passport help near Salem", route: "/cities/salem" },
    { name: "Rush passport service in Troutdale", route: "#" },
    { name: "Fast passport renewal for east Portland metro travelers", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Gresham or Troutdale — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Gresham and all of the east Portland metro remotely",
  serviceAreaIntro: "Whether you are in downtown Gresham, Troutdale, Fairview, Sandy, Boring, or anywhere in the east Portland metro and Multnomah County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Gresham-area zip codes. Anywhere in Multnomah, Clackamas, or eastern Washington County — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Gresham as simple as possible. Most Gresham clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Gresham travelers. Fast Passport Center reviews your documents before submission to catch these issues before they become problems.",
  testimonialsIntro: "Real reviews from Gresham, Troutdale, and east Portland metro travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Gresham and east Portland metro travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
};